/**
 * <s-footer-accordion> — footer link columns that collapse into an accordion on phones.
 *
 * Wraps the footer's <details data-footer-section> columns. Below 768px each column is a
 * collapsed disclosure (tap the heading to open; opening one closes the others). From 768px up every column is forced open
 * and its summary is taken out of the tab order, so it reads as a plain heading.
 * Markup ships with `open` so the columns are visible without JavaScript.
 */
class SFooterAccordion extends HTMLElement {
  connectedCallback() {
    this.media = window.matchMedia('(min-width: 48rem)');
    this.onMediaChange = () => this.sync();
    this.onToggle = (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.hasAttribute('data-footer-section')) return;
      if (this.media.matches) {
        if (!details.open) details.open = true;
        return;
      }
      // Phones: one section at a time. Opening a section closes the others.
      if (details.open) {
        this.querySelectorAll('details[data-footer-section][open]').forEach((other) => {
          if (other !== details) other.open = false;
        });
      }
    };
    this.media.addEventListener('change', this.onMediaChange);
    this.addEventListener('toggle', this.onToggle, true);
    this.sync();
  }

  disconnectedCallback() {
    this.media?.removeEventListener('change', this.onMediaChange);
    this.removeEventListener('toggle', this.onToggle, true);
  }

  sync() {
    const desktop = this.media.matches;
    this.querySelectorAll('details[data-footer-section]').forEach((details) => {
      details.open = desktop;
      const summary = details.querySelector('summary');
      if (!summary) return;
      if (desktop) {
        summary.setAttribute('tabindex', '-1');
      } else {
        summary.removeAttribute('tabindex');
      }
    });
  }
}

if (!customElements.get('s-footer-accordion')) customElements.define('s-footer-accordion', SFooterAccordion);
